<template>
    <div class="mb-4 mt-4">
        <h4 class="mb-3">{{ label }}</h4>
        <v-checkbox v-for="group in groups" :key="group.id" v-model="internalValue" :value="group.id"
            :label="group.name" :disabled="isCheckboxDisabled(group)" hide-details class="mt-1">
        </v-checkbox>
        <v-input v-if="required" type="hidden" :rules="[requiredRule]" v-model="internalValue" class="mt-2">
        </v-input>

        <!-- Warning for non-admin groups -->
        <v-alert v-if="hasNonAdminGroups" type="warning" dense text class="mb-2">
            {{ $t("Sinulla ei ole oikeuksia kaikkiin tämän julkaisun ryhmiin.") }}
        </v-alert>
        <br style="clear: both;">
    </div>
</template>

<script>
import { ref, watch, computed } from "@vue/composition-api";
import { i18n } from "@/plugins/i18n";
import { useGroupPermissions } from "@/composables/useGroupPermissions";

export default {
    name: "GroupSelector",
    props: {
        value: {
            type: Array,
            default: () => []
        },
        groups: {
            type: Array,
            required: true
        },
        required: {
            type: Boolean,
            default: true
        },
        label: {
            type: String,
            default: () => i18n.t('Julkaise ryhmiin')
        }
    },
    setup(props, { emit }) {
        const internalValue = ref(props.value || []);

        // Check permissions for selected groups
        const selectedGroupIds = computed(() => internalValue.value);
        const { hasNonAdminGroups } = useGroupPermissions(selectedGroupIds);

        // Find the "all groups" group (group_type === 1)
        const primaryGroupId = computed(() => {
            return props.groups.find(g => g.group_type === 1);
        });

        // Watch for external changes
        watch(() => props.value, (newValue) => {
            if (JSON.stringify(newValue) !== JSON.stringify(internalValue.value)) {
                internalValue.value = newValue || [];
            }
        });

        // Handle all-groups selection logic
        watch(internalValue, (newValue, oldValue) => {
            if (primaryGroupId.value) {
                const allGroupsId = primaryGroupId.value.id;
                const hasAllGroups = newValue.includes(allGroupsId);
                const hadAllGroups = oldValue ? oldValue.includes(allGroupsId) : false;

                // If all-groups was just selected, keep only that selection
                if (hasAllGroups && !hadAllGroups) {
                    internalValue.value = [allGroupsId];
                }
                // If all-groups is selected and user tries to select another, keep only the new selection
                else if (hasAllGroups && newValue.length > 1) {
                    internalValue.value = newValue.filter(id => id !== allGroupsId);
                }
            }

            // Emit changes to parent
            emit('input', internalValue.value);
        }, { deep: true });

        // Determine if a checkbox should be disabled
        const isCheckboxDisabled = (group) => {
            if (!primaryGroupId.value) return false;

            const hasAllGroups = internalValue.value.includes(primaryGroupId.value.id);

            // If all-groups is selected, disable all others
            if (hasAllGroups && group.id !== primaryGroupId.value.id) {
                return true;
            }

            return false;
        };

        const requiredRule = (v) => {
            return (v && v.length > 0) || i18n.t("Valitse vähintään yksi ryhmä");
        };

        return {
            internalValue,
            isCheckboxDisabled,
            requiredRule,
            hasNonAdminGroups
        };
    }
};
</script>
